import React, { useState, useEffect, useRef } from 'react';
import { useToast, ToastContainer } from '../Toast';
import './ModernMessagesPage.css';

// Fonction utilitaire pour lire le token du localStorage
function getAuthHeader() {
  try {
    // Essayer d'abord la structure principale du localStorage
    const authData = localStorage.getItem('authData');
    if (authData) {
      const data = JSON.parse(authData);
      if (data?.token && data?.tokenType) {
        return { Authorization: `${data.tokenType} ${data.token}` };
      }
    }

    // Fallback sur d'autres clés possibles
    const storages = [window.localStorage, window.sessionStorage];
    const keys = ['auth','userData','senoffre_auth','loginData','currentUser','user','userDetails'];
    for (const storage of storages) {
      for (const key of keys) {
        const raw = storage.getItem(key);
        if (!raw) continue;
        const data = JSON.parse(raw);
        if (data?.token) return { Authorization: `${data.tokenType || 'Bearer'} ${data.token}` };
      }
    }
    return {};
  } catch (error) {
    console.error('Erreur lors de la récupération du token:', error);
    return {};
  }
}

const MessagesPage = ({ apiEndpoint = '' }) => {
    const { toasts, showToast, removeToast } = useToast();
    const [threads, setThreads] = useState([]);
    const [selectedThread, setSelectedThread] = useState(null);
    const [messages, setMessages] = useState([]);
    const [newMessage, setNewMessage] = useState('');
    const [loading, setLoading] = useState(false);
    const [loadingMessages, setLoadingMessages] = useState(false);
    const [sendingMessage, setSendingMessage] = useState(false);
    const [searchTerm, setSearchTerm] = useState('');
    const [currentUserId, setCurrentUserId] = useState(null);
    const [userProfiles, setUserProfiles] = useState({}); // Cache pour les profils utilisateurs
    const messagesEndRef = useRef(null); // Référence pour le scroll automatique
    const [showEmojiPicker, setShowEmojiPicker] = useState(false); // État du sélecteur d'emojis
    const [selectedThreadIds, setSelectedThreadIds] = useState([]); // IDs des threads sélectionnés
    const [isSelectionMode, setIsSelectionMode] = useState(false); // Mode sélection multiple

    // Fonction pour faire défiler vers le bas
    const scrollToBottom = () => {
        messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
    };

    // Faire défiler vers le bas quand les messages changent
    useEffect(() => {
        scrollToBottom();
    }, [messages]);

    // Fermer le sélecteur d'emojis quand on clique ailleurs
    useEffect(() => {
        const handleClickOutside = (event) => {
            if (showEmojiPicker && !event.target.closest('.emoji-container')) {
                setShowEmojiPicker(false);
            }
        };

        document.addEventListener('mousedown', handleClickOutside);
        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, [showEmojiPicker]);

    // Liste des emojis populaires
    const popularEmojis = [
        '😀', '😃', '😄', '😁', '😊', '😍', '🥰', '😘', 
        '😂', '🤣', '😭', '😢', '😔', '😕', '😒', '🙄',
        '😎', '🤔', '🤗', '🤝', '👍', '👎', '👌', '✌️',
        '🙏', '💪', '👏', '🎉', '🎊', '❤️', '💕', '💯',
        '🔥', '⭐', '✨', '💫', '🌟', '⚡', '💥', '💢'
    ];

    // Fonction pour ajouter un emoji au message
    const addEmoji = (emoji) => {
        setNewMessage(prev => prev + emoji);
        setShowEmojiPicker(false);
    };

    // Récupérer l'ID de l'utilisateur connecté
    const getCurrentUserId = () => {
        try {
            const storages = [window.localStorage, window.sessionStorage];
            const keys = ['authData','auth','userData','senoffre_auth','loginData','currentUser','user','userDetails'];
            
            for (const storage of storages) {
                for (const key of keys) {
                    const raw = storage.getItem(key);
                    if (!raw) continue;
                    const data = JSON.parse(raw);
                    
                    // Chercher l'ID utilisateur dans différentes structures possibles
                    if (data?.user?.id) return data.user.id;
                    if (data?.id) return data.id;
                    if (data?.userId) return data.userId;
                }
            }
            return null;
        } catch (error) {
            console.error('Erreur lors de la récupération de l\'ID utilisateur:', error);
            return null;
        }
    };

    // Récupérer les détails d'un utilisateur
    const fetchUserProfile = async (userId) => {
        // Vérifier si on a déjà les données en cache
        if (userProfiles[userId]) {
            return userProfiles[userId];
        }

        try {
            const authHeaders = getAuthHeader();
            
            const response = await fetch(`${apiEndpoint}/users/${userId}`, {
                headers: { 
                    'Accept': 'application/json',
                    ...authHeaders
                },
            });
            
            if (response.ok) {
                const userData = await response.json();
                console.log(`Profil utilisateur ${userId}:`, userData);
                
                // Utiliser la structure correcte de votre API
                const userInfo = userData?.result || userData?.data || userData;
                
                const profileData = {
                    id: userInfo.id,
                    name: userInfo.name || 'Utilisateur',
                    email: userInfo.email,
                    avatar: userInfo.photo_url || userInfo.photo,
                    company: userInfo.about || '',
                    role: userInfo.user_type_id === 1 ? 'Recruteur' : 'Candidat'
                };
                
                // Mettre en cache les données utilisateur
                setUserProfiles(prev => ({
                    ...prev,
                    [userId]: profileData
                }));
                
                return profileData;
            } else {
                console.warn(`Impossible de récupérer le profil utilisateur ${userId}`);
                return null;
            }
        } catch (error) {
            console.error(`Erreur lors de la récupération du profil utilisateur ${userId}:`, error);
            return null;
        }
    };

    // Récupérer la liste des threads
    const fetchThreads = async () => {
        setLoading(true);
        try {
            const authHeaders = getAuthHeader();
            
            if (!authHeaders.Authorization) {
                showToast('Vous devez être connecté pour voir vos messages', 'error');
                setThreads([]);
                return;
            }

            const response = await fetch(`${apiEndpoint}/threads`, {
                headers: { 
                    'Accept': 'application/json',
                    ...authHeaders
                },
            });
            
            if (response.ok) {
                const data = await response.json();
                console.log('Threads API Response:', data);
                
                // Extraire les threads selon la structure de la réponse
                let threadsList = data?.result?.data || data?.data || data?.result || data || [];
                
                if (!Array.isArray(threadsList)) {
                    if (threadsList && Array.isArray(threadsList.data)) threadsList = threadsList.data;
                    else threadsList = [];
                }
                
                console.log('Threads extraits:', threadsList);
                setThreads(threadsList);
                
                // Récupérer les profils des créateurs de threads
                const creatorIds = [...new Set(threadsList.map(thread => thread.p_creator?.id).filter(Boolean))];
                for (const userId of creatorIds) {
                    if (userId && userId !== currentUserId) {
                        await fetchUserProfile(userId);
                    }
                }
            } else {
                throw new Error(`Erreur ${response.status}`);
            }
        } catch (e) {
            console.error('[Threads API] fetch error:', e);
            showToast('Impossible de charger les conversations', 'error');
            setThreads([]);
        } finally {
            setLoading(false);
        }
    };

    // Récupérer les détails d'un thread et ses messages
    const fetchThreadDetails = async (threadId) => {
        setLoadingMessages(true);
        try {
            const authHeaders = getAuthHeader();
            
            const response = await fetch(`${apiEndpoint}/threads/${threadId}`, {
                headers: { 
                    'Accept': 'application/json',
                    ...authHeaders
                },
            });
            
            if (response.ok) {
                const threadData = await response.json();
                console.log('Détails du thread:', threadData);
                
                // Récupérer les messages du thread
                const messagesResponse = await fetch(`${apiEndpoint}/threads/${threadId}/messages`, {
                    headers: { 
                        'Accept': 'application/json',
                        ...authHeaders
                    },
                });
                
                if (messagesResponse.ok) {
                    const messagesData = await messagesResponse.json();
                    let messagesList = messagesData?.result?.data || messagesData?.data || messagesData?.result || messagesData || [];
                    
                    if (!Array.isArray(messagesList)) {
                        if (messagesList && Array.isArray(messagesList.data)) messagesList = messagesList.data;
                        else messagesList = [];
                    }
                    
                    console.log('Messages récupérés:', messagesList);
                    setMessages(messagesList);
                    
                    // Récupérer les profils des utilisateurs pour chaque message
                    const uniqueUserIds = [...new Set(messagesList.map(msg => msg.user_id).filter(Boolean))];
                    for (const userId of uniqueUserIds) {
                        if (userId && userId !== currentUserId) {
                            await fetchUserProfile(userId);
                        }
                    }
                } else {
                    setMessages([]);
                }
            } else {
                throw new Error(`Erreur ${response.status}`);
            }
        } catch (e) {
            console.error('[Thread Details API] fetch error:', e);
            showToast('Impossible de charger les détails de la conversation', 'error');
            setMessages([]);
        } finally {
            setLoadingMessages(false);
        }
    };

    // Charger les threads et l'ID utilisateur au montage du composant
    useEffect(() => {
        const userId = getCurrentUserId();
        setCurrentUserId(userId);
        console.log('ID utilisateur connecté:', userId);
        fetchThreads();
    }, []);

    // Sélectionner un thread et charger ses messages
    const handleSelectThread = async (thread) => {
        setSelectedThread(thread);
        await fetchThreadDetails(thread.id);
    };

    // Envoyer un message
    const sendMessage = async () => {
        if (!newMessage.trim() || !selectedThread || sendingMessage) return;

        setSendingMessage(true);
        try {
            const authHeaders = getAuthHeader();
            
            const response = await fetch(`${apiEndpoint}/threads/${selectedThread.id}/messages`, {
                method: 'POST',
                headers: { 
                    'Content-Type': 'application/json',
                    'Accept': 'application/json',
                    ...authHeaders
                },
                body: JSON.stringify({
                    body: newMessage
                })
            });
            
            if (response.ok) {
                const result = await response.json();
                console.log('Message envoyé avec succès:', result);
                
                // Ajouter le nouveau message à la liste locale
                const newMsg = {
                    id: Date.now(), // ID temporaire
                    body: newMessage,
                    created_at: new Date().toISOString(),
                    user_id: currentUserId, // ID de l'utilisateur actuel
                    // Autres propriétés selon la structure API
                };
                
                setMessages(prev => [...prev, newMsg]);
                setNewMessage('');
                showToast('Message envoyé avec succès !', 'success');
                
                // Optionnel : recharger les messages pour avoir les données exactes de l'API
                // await fetchThreadDetails(selectedThread.id);
            } else {
                throw new Error(`Erreur ${response.status}`);
            }
        } catch (e) {
            console.error('[Send Message API] error:', e);
            showToast('Erreur lors de l\'envoi du message', 'error');
        } finally {
            setSendingMessage(false);
        }
    };

    // ========== NOUVELLES FONCTIONS API POUR LA MESSAGERIE ==========

    // Supprimer un ou plusieurs threads
    const deleteThreads = async (threadIds) => {
        try {
            const authHeaders = getAuthHeader();
            const idsString = Array.isArray(threadIds) ? threadIds.join(',') : threadIds;
            
            const response = await fetch(`${apiEndpoint}/threads/${idsString}`, {
                method: 'DELETE',
                headers: { 
                    'Accept': 'application/json',
                    ...authHeaders
                }
            });
            
            if (response.ok) {
                const result = await response.json();
                console.log('Threads supprimés avec succès:', result);
                
                // Mettre à jour la liste locale
                const idsArray = Array.isArray(threadIds) ? threadIds : [threadIds];
                setThreads(prev => prev.filter(thread => !idsArray.includes(thread.id)));
                
                // Si le thread sélectionné est supprimé, le désélectionner
                if (selectedThread && idsArray.includes(selectedThread.id)) {
                    setSelectedThread(null);
                    setMessages([]);
                }
                
                const count = idsArray.length;
                showToast(`${count} conversation${count > 1 ? 's' : ''} supprimée${count > 1 ? 's' : ''} avec succès`, 'success');
                
                return true;
            } else {
                throw new Error(`Erreur ${response.status}`);
            }
        } catch (e) {
            console.error('[Delete Threads API] error:', e);
            showToast('Erreur lors de la suppression', 'error');
            return false;
        }
    };

    // Marquer comme lu un ou plusieurs threads
    const markThreadsAsRead = async (threadIds) => {
        try {
            const authHeaders = getAuthHeader();
            const idsString = Array.isArray(threadIds) ? threadIds.join(',') : threadIds;
            
            const response = await fetch(`${apiEndpoint}/threads/bulkUpdate/${idsString}?type=markAsRead`, {
                method: 'POST',
                headers: { 
                    'Accept': 'application/json',
                    ...authHeaders
                }
            });
            
            if (response.ok) {
                const result = await response.json();
                console.log('Threads marqués comme lus:', result);
                
                // Mettre à jour la liste locale
                const idsArray = Array.isArray(threadIds) ? threadIds : [threadIds];
                setThreads(prev => prev.map(thread => 
                    idsArray.includes(thread.id) 
                        ? { ...thread, p_is_unread: false }
                        : thread
                ));
                
                const count = idsArray.length;
                showToast(`${count} conversation${count > 1 ? 's' : ''} marquée${count > 1 ? 's' : ''} comme lue${count > 1 ? 's' : ''}`, 'success');
                
                return true;
            } else {
                throw new Error(`Erreur ${response.status}`);
            }
        } catch (e) {
            console.error('[Mark as Read API] error:', e);
            showToast('Erreur lors du marquage comme lu', 'error');
            return false;
        }
    };

    // Marquer comme non lu un ou plusieurs threads
    const markThreadsAsUnread = async (threadIds) => {
        try {
            const authHeaders = getAuthHeader();
            const idsString = Array.isArray(threadIds) ? threadIds.join(',') : threadIds;
            
            const response = await fetch(`${apiEndpoint}/threads/bulkUpdate/${idsString}?type=markAsUnread`, {
                method: 'POST',
                headers: { 
                    'Accept': 'application/json',
                    ...authHeaders
                }
            });
            
            if (response.ok) {
                const result = await response.json();
                console.log('Threads marqués comme non lus:', result);
                
                // Mettre à jour la liste locale
                const idsArray = Array.isArray(threadIds) ? threadIds : [threadIds];
                setThreads(prev => prev.map(thread => 
                    idsArray.includes(thread.id) 
                        ? { ...thread, p_is_unread: true }
                        : thread
                ));
                
                const count = idsArray.length;
                showToast(`${count} conversation${count > 1 ? 's' : ''} marquée${count > 1 ? 's' : ''} comme non lue${count > 1 ? 's' : ''}`, 'success');
                
                return true;
            } else {
                throw new Error(`Erreur ${response.status}`);
            }
        } catch (e) {
            console.error('[Mark as Unread API] error:', e);
            showToast('Erreur lors du marquage comme non lu', 'error');
            return false;
        }
    };

    // Tout marquer comme lu
    const markAllAsRead = async () => {
        try {
            const authHeaders = getAuthHeader();
            
            const response = await fetch(`${apiEndpoint}/threads/bulkUpdate?type=markAllAsRead`, {
                method: 'POST',
                headers: { 
                    'Accept': 'application/json',
                    ...authHeaders
                }
            });
            
            if (response.ok) {
                const result = await response.json();
                console.log('Tous les threads marqués comme lus:', result);
                
                // Mettre à jour la liste locale
                setThreads(prev => prev.map(thread => ({ ...thread, p_is_unread: false })));
                
                showToast('Toutes les conversations marquées comme lues', 'success');
                
                return true;
            } else {
                throw new Error(`Erreur ${response.status}`);
            }
        } catch (e) {
            console.error('[Mark All as Read API] error:', e);
            showToast('Erreur lors du marquage global comme lu', 'error');
            return false;
        }
    };

    // Marquer comme important un ou plusieurs threads
    const markThreadsAsImportant = async (threadIds) => {
        try {
            const authHeaders = getAuthHeader();
            const idsString = Array.isArray(threadIds) ? threadIds.join(',') : threadIds;
            
            const response = await fetch(`${apiEndpoint}/threads/bulkUpdate/${idsString}?type=markAsImportant`, {
                method: 'POST',
                headers: { 
                    'Accept': 'application/json',
                    ...authHeaders
                }
            });
            
            if (response.ok) {
                const result = await response.json();
                console.log('Threads marqués comme importants:', result);
                
                // Mettre à jour la liste locale
                const idsArray = Array.isArray(threadIds) ? threadIds : [threadIds];
                setThreads(prev => prev.map(thread => 
                    idsArray.includes(thread.id) 
                        ? { ...thread, p_is_important: true }
                        : thread
                ));
                
                const count = idsArray.length;
                showToast(`${count} conversation${count > 1 ? 's' : ''} marquée${count > 1 ? 's' : ''} comme importante${count > 1 ? 's' : ''}`, 'success');
                
                return true;
            } else {
                throw new Error(`Erreur ${response.status}`);
            }
        } catch (e) {
            console.error('[Mark as Important API] error:', e);
            showToast('Erreur lors du marquage comme important', 'error');
            return false;
        }
    };

    // Retirer le statut important un ou plusieurs threads
    const markThreadsAsNotImportant = async (threadIds) => {
        try {
            const authHeaders = getAuthHeader();
            const idsString = Array.isArray(threadIds) ? threadIds.join(',') : threadIds;
            
            const response = await fetch(`${apiEndpoint}/threads/bulkUpdate/${idsString}?type=markAsNotImportant`, {
                method: 'POST',
                headers: { 
                    'Accept': 'application/json',
                    ...authHeaders
                }
            });
            
            if (response.ok) {
                const result = await response.json();
                console.log('Statut important retiré:', result);
                
                // Mettre à jour la liste locale
                const idsArray = Array.isArray(threadIds) ? threadIds : [threadIds];
                setThreads(prev => prev.map(thread => 
                    idsArray.includes(thread.id) 
                        ? { ...thread, p_is_important: false }
                        : thread
                ));
                
                const count = idsArray.length;
                showToast(`Statut important retiré de ${count} conversation${count > 1 ? 's' : ''}`, 'success');
                
                return true;
            } else {
                throw new Error(`Erreur ${response.status}`);
            }
        } catch (e) {
            console.error('[Mark as Not Important API] error:', e);
            showToast('Erreur lors du retrait du statut important', 'error');
            return false;
        }
    };

    // ========== FIN DES NOUVELLES FONCTIONS API ==========

    // ========== FONCTIONS DE GESTION DE LA SÉLECTION MULTIPLE ==========

    // Activer/désactiver le mode sélection
    const toggleSelectionMode = () => {
        setIsSelectionMode(!isSelectionMode);
        setSelectedThreadIds([]);
    };

    // Sélectionner/désélectionner un thread
    const toggleThreadSelection = (threadId) => {
        setSelectedThreadIds(prev => {
            if (prev.includes(threadId)) {
                return prev.filter(id => id !== threadId);
            } else {
                return [...prev, threadId];
            }
        });
    };

    // Sélectionner tous les threads
    const selectAllThreads = () => {
        const allThreadIds = filteredThreads.map(thread => thread.id);
        setSelectedThreadIds(allThreadIds);
    };

    // Désélectionner tous les threads
    const deselectAllThreads = () => {
        setSelectedThreadIds([]);
    };

    // Actions en lot pour les threads sélectionnés
    const handleBulkAction = async (action) => {
        if (selectedThreadIds.length === 0) {
            showToast('Aucune conversation sélectionnée', 'warning');
            return;
        }

        let success = false;
        switch (action) {
            case 'delete':
                if (window.confirm(`Êtes-vous sûr de vouloir supprimer ${selectedThreadIds.length} conversation(s) ?`)) {
                    success = await deleteThreads(selectedThreadIds);
                }
                break;
            case 'markAsRead':
                success = await markThreadsAsRead(selectedThreadIds);
                break;
            case 'markAsUnread':
                success = await markThreadsAsUnread(selectedThreadIds);
                break;
            case 'markAsImportant':
                success = await markThreadsAsImportant(selectedThreadIds);
                break;
            case 'markAsNotImportant':
                success = await markThreadsAsNotImportant(selectedThreadIds);
                break;
            default:
                showToast('Action non reconnue', 'error');
                return;
        }

        if (success) {
            setSelectedThreadIds([]);
            setIsSelectionMode(false);
        }
    };

    // Actions individuelles pour un thread
    const handleSingleAction = async (threadId, action) => {
        let success = false;
        switch (action) {
            case 'delete':
                if (window.confirm('Êtes-vous sûr de vouloir supprimer cette conversation ?')) {
                    success = await deleteThreads(threadId);
                }
                break;
            case 'markAsRead':
                success = await markThreadsAsRead(threadId);
                break;
            case 'markAsUnread':
                success = await markThreadsAsUnread(threadId);
                break;
            case 'markAsImportant':
                success = await markThreadsAsImportant(threadId);
                break;
            case 'markAsNotImportant':
                success = await markThreadsAsNotImportant(threadId);
                break;
            default:
                showToast('Action non reconnue', 'error');
                return;
        }
        return success;
    };

    // ========== FIN DES FONCTIONS DE SÉLECTION MULTIPLE ==========

    const getCompanyInitials = (companyName) => {
        if (!companyName) return '??';
        return companyName.split(' ').map(word => word.charAt(0)).join('').substring(0, 2).toUpperCase();
    };

    // Obtenir les informations utilisateur (nom, avatar) pour un message
    const getUserInfo = (userId) => {
        if (!userId) return { name: 'Utilisateur', avatar: null };
        
        const profile = userProfiles[userId];
        if (profile) {
            return {
                name: profile.name,
                avatar: profile.avatar,
                company: profile.company,
                role: profile.role
            };
        }
        
        return { name: 'Utilisateur', avatar: null };
    };

    // Obtenir les informations du créateur d'un thread
    const getThreadCreatorInfo = (thread) => {
        if (!thread?.p_creator) return { name: 'Utilisateur', avatar: null };
        
        const creator = thread.p_creator;
        const userId = creator.id;
        
        // Vérifier d'abord dans le cache des profils (données de l'API /users/{id})
        const cachedProfile = userProfiles[userId];
        if (cachedProfile && cachedProfile.name !== 'Utilisateur') {
            console.log(`Utilisation du cache pour l'utilisateur ${userId}:`, cachedProfile);
            return {
                name: cachedProfile.name,
                avatar: cachedProfile.avatar,
                company: cachedProfile.company,
                role: cachedProfile.role
            };
        }
        
        // Utiliser les données directement du p_creator (déjà disponibles)
        const directData = {
            name: creator.name || 'Utilisateur',
            avatar: creator.photo_url || creator.photo,
            company: creator.about || '',
            role: creator.user_type_id === 1 ? 'Recruteur' : 'Candidat'
        };
        
        console.log(`Utilisation des données directes pour l'utilisateur ${userId}:`, directData);
        return directData;
    };

    const formatTime = (timestamp) => {
        if (!timestamp) return '';
        try {
            const date = new Date(timestamp);
            const now = new Date();
            const diffInHours = (now - date) / (1000 * 60 * 60);
            
            if (diffInHours < 24) {
                return date.toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' });
            } else {
                return date.toLocaleDateString('fr-FR', { day: '2-digit', month: '2-digit' });
            }
        } catch (e) {
            return '';
        }
    };

    // Filtrer les threads selon le terme de recherche
    const filteredThreads = threads.filter(thread => {
        if (!searchTerm) return true;
        const searchLower = searchTerm.toLowerCase();
        return (
            (thread.subject && thread.subject.toLowerCase().includes(searchLower)) ||
            (thread.company_name && thread.company_name.toLowerCase().includes(searchLower)) ||
            (thread.last_message && thread.last_message.toLowerCase().includes(searchLower))
        );
    });

    return (
        <div className="modern-messages-container">
            {loading ? (
                <div className="loading-state">
                    <div className="loading-spinner"></div>
                    <p>Chargement de vos messages...</p>
                </div>
            ) : (
                <div className="messages-layout">
                    {/* Sidebar Navigation */}
                    <div className="messages-sidebar">
                        <div className="sidebar-header">
                            <h2 className="sidebar-title">
                                <i className="fas fa-envelope"></i>
                                Messages
                            </h2>
                            <button 
                                className="refresh-btn"
                                onClick={fetchThreads}
                                disabled={loading}
                                title="Actualiser"
                            >
                                <i className={`fas fa-sync-alt ${loading ? 'fa-spin' : ''}`}></i>
                            </button>
                        </div>
                        
                        <div className="sidebar-stats">
                            <div className="stat-item">
                                <span className="stat-label">Inbox</span>
                                <span className="stat-count">{filteredThreads.length}</span>
                            </div>
                        </div>
                        
                        <div className="sidebar-filters">
                            <div className="filter-item active">
                                <i className="fas fa-inbox"></i>
                                <span>Tous les messages</span>
                            </div>
                            <div className="filter-item">
                                <i className="fas fa-star"></i>
                                <span>Importants</span>
                            </div>
                            <div className="filter-item">
                                <i className="fas fa-archive"></i>
                                <span>Archivés</span>
                            </div>
                        </div>
                    </div>

                    {/* Main Content Area */}
                    <div className="messages-main">
                        {threads.length === 0 ? (
                            <div className="empty-state">
                                <div className="empty-icon">
                                    <i className="far fa-envelope"></i>
                                </div>
                                <h3>Aucun message</h3>
                                <p>Vos conversations avec les recruteurs apparaîtront ici</p>
                            </div>
                        ) : !selectedThread ? (
                            <div className="messages-content">
                                {/* Header avec actions */}
                                <div className="content-header">
                                    <div className="header-info">
                                        <h3>Inbox</h3>
                                        <span className="message-count">
                                            {filteredThreads.length} messages
                                            {selectedThreadIds.length > 0 && (
                                                <span className="selected-count"> • {selectedThreadIds.length} sélectionnés</span>
                                            )}
                                        </span>
                                    </div>
                                    <div className="header-actions">
                                        {/* Actions en lot quand des threads sont sélectionnés */}
                                        {isSelectionMode && selectedThreadIds.length > 0 && (
                                            <div className="bulk-actions">
                                                <button 
                                                    className="bulk-action-btn read"
                                                    onClick={() => handleBulkAction('markAsRead')}
                                                    title="Marquer comme lu"
                                                >
                                                    <i className="fas fa-envelope-open"></i>
                                                </button>
                                                <button 
                                                    className="bulk-action-btn unread"
                                                    onClick={() => handleBulkAction('markAsUnread')}
                                                    title="Marquer comme non lu"
                                                >
                                                    <i className="fas fa-envelope"></i>
                                                </button>
                                                <button 
                                                    className="bulk-action-btn important"
                                                    onClick={() => handleBulkAction('markAsImportant')}
                                                    title="Marquer comme important"
                                                >
                                                    <i className="fas fa-star"></i>
                                                </button>
                                                <button 
                                                    className="bulk-action-btn not-important"
                                                    onClick={() => handleBulkAction('markAsNotImportant')}
                                                    title="Retirer le statut important"
                                                >
                                                    <i className="far fa-star"></i>
                                                </button>
                                                <button 
                                                    className="bulk-action-btn delete"
                                                    onClick={() => handleBulkAction('delete')}
                                                    title="Supprimer"
                                                >
                                                    <i className="fas fa-trash"></i>
                                                </button>
                                            </div>
                                        )}
                                        
                                        {/* Actions normales */}
                                        <div className="normal-actions">
                                            <div className="search-container">
                                                <i className="fas fa-search"></i>
                                                <input 
                                                    type="text" 
                                                    placeholder="Rechercher..."
                                                    value={searchTerm}
                                                    onChange={(e) => setSearchTerm(e.target.value)}
                                                />
                                            </div>
                                            
                                            {/* Bouton sélection multiple */}
                                            <button 
                                                className={`action-btn ${isSelectionMode ? 'active' : ''}`}
                                                onClick={toggleSelectionMode}
                                                title={isSelectionMode ? 'Quitter la sélection' : 'Sélection multiple'}
                                            >
                                                <i className={`fas ${isSelectionMode ? 'fa-times' : 'fa-check-square'}`}></i>
                                            </button>
                                            
                                            {/* Boutons de sélection quand en mode sélection */}
                                            {isSelectionMode && (
                                                <>
                                                    <button 
                                                        className="action-btn"
                                                        onClick={selectAllThreads}
                                                        title="Tout sélectionner"
                                                    >
                                                        <i className="fas fa-check-double"></i>
                                                    </button>
                                                    <button 
                                                        className="action-btn"
                                                        onClick={deselectAllThreads}
                                                        title="Tout désélectionner"
                                                    >
                                                        <i className="far fa-square"></i>
                                                    </button>
                                                </>
                                            )}
                                            
                                            {/* Bouton tout marquer comme lu */}
                                            <button 
                                                className="action-btn"
                                                onClick={markAllAsRead}
                                                title="Tout marquer comme lu"
                                            >
                                                <i className="fas fa-envelope-open-text"></i>
                                            </button>
                                            
                                            <button className="action-btn">
                                                <i className="fas fa-filter"></i>
                                            </button>
                                            <button className="action-btn">
                                                <i className="fas fa-cog"></i>
                                            </button>
                                        </div>
                                    </div>
                                </div>
                                
                                {/* Liste des conversations */}
                                <div className="conversations-list">
                                    {filteredThreads.map((thread) => {
                                        // Debug pour voir les données du thread
                                        console.log('Thread data:', thread);
                                        console.log('p_creator data:', thread.p_creator);
                                        
                                        // Récupérer les infos du créateur du thread
                                        const creatorInfo = getThreadCreatorInfo(thread);
                                        const isUnread = thread.p_is_unread;
                                        const lastMessage = thread.latest_message?.body || 'Nouvelle conversation';
                                        
                                        console.log('Creator info résolu:', creatorInfo);
                                        
                                        return (
                                            <div 
                                                key={thread.id}
                                                className={`conversation-item ${isUnread ? 'unread' : ''} ${selectedThreadIds.includes(thread.id) ? 'selected' : ''}`}
                                                onClick={(e) => {
                                                    // Si on est en mode sélection et qu'on clique sur la checkbox, ne pas ouvrir le thread
                                                    if (isSelectionMode && (e.target.type === 'checkbox' || e.target.closest('.selection-checkbox'))) {
                                                        return;
                                                    }
                                                    // Si on est en mode sélection, sélectionner le thread au lieu de l'ouvrir
                                                    if (isSelectionMode) {
                                                        toggleThreadSelection(thread.id);
                                                    } else {
                                                        handleSelectThread(thread);
                                                    }
                                                }}
                                            >
                                                {/* Checkbox de sélection */}
                                                {isSelectionMode && (
                                                    <div className="selection-checkbox">
                                                        <input
                                                            type="checkbox"
                                                            checked={selectedThreadIds.includes(thread.id)}
                                                            onChange={() => toggleThreadSelection(thread.id)}
                                                            onClick={(e) => e.stopPropagation()}
                                                        />
                                                    </div>
                                                )}
                                                
                                                <div className="conversation-avatar">
                                                    {creatorInfo?.avatar ? (
                                                        <img src={creatorInfo.avatar} alt={creatorInfo.name} />
                                                    ) : (
                                                        <div className="avatar-fallback">
                                                            {getCompanyInitials(creatorInfo?.name || thread.subject)}
                                                        </div>
                                                    )}
                                                    <div className="status-indicator online"></div>
                                                </div>
                                                
                                                <div className="conversation-content">
                                                    <div className="conversation-header">
                                                        <div className="sender-info">
                                                            <span className="sender-name">
                                                                {creatorInfo?.name || 'Utilisateur'}
                                                            </span>
                                                            <span className="sender-role">
                                                                {creatorInfo?.role || thread.subject || 'Conversation'}
                                                            </span>
                                                        </div>
                                                        <div className="message-meta">
                                                            <span className="message-time">
                                                                {formatTime(thread.updated_at)}
                                                            </span>
                                                            {isUnread && (
                                                                <div className="unread-badge">
                                                                    1
                                                                </div>
                                                            )}
                                                        </div>
                                                    </div>
                                                    
                                                    <div className="message-preview">
                                                        <p>{lastMessage}</p>
                                                    </div>
                                                    
                                                    <div className="message-tags">
                                                        {thread.p_is_important && (
                                                            <span className="tag important">Important</span>
                                                        )}
                                                        <span className="tag category">{thread.subject}</span>
                                                    </div>
                                                </div>
                                                
                                                <div className="conversation-actions">
                                                    {/* Bouton marquer comme lu/non lu */}
                                                    <button 
                                                        className="action-btn" 
                                                        onClick={(e) => {
                                                            e.stopPropagation();
                                                            handleSingleAction(thread.id, isUnread ? 'markAsRead' : 'markAsUnread');
                                                        }}
                                                        title={isUnread ? "Marquer comme lu" : "Marquer comme non lu"}
                                                    >
                                                        <i className={`fas ${isUnread ? 'fa-envelope-open' : 'fa-envelope'}`}></i>
                                                    </button>
                                                    
                                                    {/* Bouton important */}
                                                    <button 
                                                        className="action-btn" 
                                                        onClick={(e) => {
                                                            e.stopPropagation();
                                                            handleSingleAction(thread.id, thread.p_is_important ? 'markAsNotImportant' : 'markAsImportant');
                                                        }}
                                                        title={thread.p_is_important ? "Retirer le statut important" : "Marquer comme important"}
                                                    >
                                                        <i className={`${thread.p_is_important ? 'fas' : 'far'} fa-star`}></i>
                                                    </button>
                                                    
                                                    {/* Bouton archiver (placeholder) */}
                                                    <button className="action-btn" title="Archiver">
                                                        <i className="fas fa-archive"></i>
                                                    </button>
                                                    
                                                    {/* Bouton supprimer */}
                                                    <button 
                                                        className="action-btn delete-btn" 
                                                        onClick={(e) => {
                                                            e.stopPropagation();
                                                            handleSingleAction(thread.id, 'delete');
                                                        }}
                                                        title="Supprimer"
                                                    >
                                                        <i className="far fa-trash-alt"></i>
                                                    </button>
                                                </div>
                                            </div>
                                        );
                                    })}
                                </div>
                            </div>
                        ) : (
                            /* Chat Area quand une conversation est sélectionnée */
                            <div className="chat-area-full">
                                {/* Chat Header avec bouton retour */}
                                <div className="chat-header">
                                    <div className="chat-contact">
                                        <button 
                                            className="back-btn"
                                            onClick={() => setSelectedThread(null)}
                                            title="Retour à la liste"
                                        >
                                            <i className="fas fa-arrow-left"></i>
                                        </button>
                                        <div className="contact-avatar">
                                            {(() => {
                                                const creatorInfo = getThreadCreatorInfo(selectedThread);
                                                
                                                if (creatorInfo?.avatar) {
                                                    return <img src={creatorInfo.avatar} alt={creatorInfo.name} />;
                                                } else {
                                                    return (
                                                        <div className="avatar-fallback">
                                                            {getCompanyInitials(creatorInfo?.name || selectedThread.subject)}
                                                        </div>
                                                    );
                                                }
                                            })()}
                                            <div className="status-indicator online"></div>
                                        </div>
                                        <div className="contact-info">
                                            {(() => {
                                                const creatorInfo = getThreadCreatorInfo(selectedThread);
                                                
                                                return (
                                                    <>
                                                        <h4>{creatorInfo?.name || 'Utilisateur'}</h4>
                                                        <p>{creatorInfo?.role || selectedThread.subject || 'Conversation'}</p>
                                                    </>
                                                );
                                            })()}
                                        </div>
                                    </div>
                                    <div className="chat-actions">
                                        <button className="action-btn" title="Appel vidéo">
                                            <i className="fas fa-video"></i>
                                        </button>
                                        <button className="action-btn" title="Appel audio">
                                            <i className="fas fa-phone"></i>
                                        </button>
                                        <button className="action-btn" title="Plus d'options">
                                            <i className="fas fa-ellipsis-v"></i>
                                        </button>
                                    </div>
                                </div>
                                
                                {/* Messages Container */}
                                <div className="chat-messages">
                                    {loadingMessages ? (
                                        <div className="loading-messages">
                                            <div className="loading-spinner"></div>
                                            <p>Chargement des messages...</p>
                                        </div>
                                    ) : messages.length === 0 ? (
                                        <div className="no-messages">
                                            <div className="no-messages-icon">
                                                <i className="far fa-comments"></i>
                                            </div>
                                            <p>Commencez votre conversation</p>
                                        </div>
                                    ) : (
                                        <div className="messages-list">
                                            {messages.map((message, index) => {
                                                const isCurrentUser = message.user_id == currentUserId;
                                                const userInfo = !isCurrentUser ? getUserInfo(message.user_id) : null;
                                                
                                                return (
                                                    <div 
                                                        key={message.id || index} 
                                                        className={`message-bubble ${isCurrentUser ? 'sent' : 'received'}`}
                                                    >
                                                        {!isCurrentUser && (
                                                            <div className="message-avatar">
                                                                {userInfo?.avatar ? (
                                                                    <img src={userInfo.avatar} alt={userInfo.name} />
                                                                ) : (
                                                                    <div className="avatar-fallback small">
                                                                        {getCompanyInitials(userInfo?.name || selectedThread.company_name || 'R')}
                                                                    </div>
                                                                )}
                                                            </div>
                                                        )}
                                                        <div className="message-content">
                                                            {!isCurrentUser && userInfo?.name && (
                                                                <div className="message-sender">
                                                                    {userInfo.name}
                                                                </div>
                                                            )}
                                                            <div className="message-text">
                                                                {message.body || message.content}
                                                            </div>
                                                            <div className="message-time">
                                                                {formatTime(message.created_at || message.timestamp)}
                                                            </div>
                                                        </div>
                                                    </div>
                                                );
                                            })}
                                            {/* Référence pour le scroll automatique */}
                                            <div ref={messagesEndRef} />
                                        </div>
                                    )}
                                </div>
                                
                                {/* Message Input */}
                                <div className="chat-input">
                                    <div className="input-container">
                                        <button className="attachment-btn" title="Joindre un fichier">
                                            <i className="fas fa-paperclip"></i>
                                        </button>
                                        <input 
                                            type="text" 
                                            placeholder="Écrire un message..."
                                            value={newMessage}
                                            onChange={(e) => setNewMessage(e.target.value)}
                                            onKeyPress={(e) => e.key === 'Enter' && sendMessage()}
                                            disabled={sendingMessage}
                                        />
                                        <div className="emoji-container">
                                            <button 
                                                className="emoji-btn" 
                                                title="Émojis"
                                                onClick={() => setShowEmojiPicker(!showEmojiPicker)}
                                            >
                                                <i className="far fa-smile"></i>
                                            </button>
                                            {showEmojiPicker && (
                                                <div className="emoji-picker">
                                                    <div className="emoji-grid">
                                                        {popularEmojis.map((emoji, index) => (
                                                            <button
                                                                key={index}
                                                                className="emoji-item"
                                                                onClick={() => addEmoji(emoji)}
                                                                title={emoji}
                                                            >
                                                                {emoji}
                                                            </button>
                                                        ))}
                                                    </div>
                                                </div>
                                            )}
                                        </div>
                                        <button 
                                            className={`send-btn ${sendingMessage ? 'sending' : ''}`}
                                            onClick={sendMessage}
                                            disabled={sendingMessage || !newMessage.trim()}
                                            title="Envoyer"
                                        >
                                            <i className={`fas ${sendingMessage ? 'fa-spinner fa-spin' : 'fa-paper-plane'}`}></i>
                                        </button>
                                    </div>
                                </div>
                            </div>
                        )}

                    </div>
                </div>
            )}
            
            {/* Container pour les toasts */}
            <ToastContainer toasts={toasts} removeToast={removeToast} />
        </div>
    );
};

export default MessagesPage;
